import { useState } from 'react';
import { ErrorState, Input, Modal, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { ContentItem } from '../../domain/entities/content.entity';
import { useExams } from '../hooks/use-exams';
import { useInstitutionsById } from '../hooks/use-institutions';
import { formatInstitutionLabel, readExamBody } from '../exam-body';
import {
  ContentStatusBadge,
  InstitutionCell,
  getApiErrorMessage,
} from '../display';

/**
 * Página única grande no lugar de paginação: o filtro é client-side (o `body` da
 * prova é `Mixed` e não-indexado no Mongo, então não há como filtrar
 * instituição/ano/variante no servidor hoje — ver a pendência
 * "filtro de listagem de provas" no plano).
 */
const PICKER_PAGE_SIZE = 100;

/**
 * Escolha de uma prova real: o destino de uma transferência de questões (os
 * textos padrão) ou o filtro "questões da prova" da listagem. As colunas
 * da listagem de provas que cabem no modal; a linha inteira é o botão "escolher" (o `Table` do
 * design-system não expõe className por linha, então um botão que só aparece no
 * hover exigiria mudar o design-system — a linha clicável já tem o realce de
 * hover embutido).
 */
export function ExamPickerModal({
  open,
  onClose,
  excludeExamId,
  onPick,
  title = 'Transferir para qual prova?',
  description = 'Clique na prova de destino. As questões entram no fim da lista dela.',
  emptyMessage = 'Nenhuma outra prova real encontrada — simulado não recebe transferência.',
}: {
  open: boolean;
  onClose: () => void;
  /** Na transferência, a prova de origem não aparece: mover para ela mesma não é uma operação. */
  excludeExamId?: string;
  onPick: (exam: ContentItem) => void;
  title?: string;
  description?: string;
  emptyMessage?: string;
}) {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, error, refetch } = useExams({
    page: 1,
    perPage: PICKER_PAGE_SIZE,
    includeArchived: true,
  });
  const { data: institutionsById } = useInstitutionsById();

  const term = search.trim().toLowerCase();
  const exams = (data?.items ?? [])
    .filter((exam) => exam.id !== excludeExamId)
    // Transferir re-possui a questão (`CONTAINS` no destino), e só prova real é
    // dona — simulado aponta. O back recusa simulado como destino
    // (`INVALID_CONTENT_LINK_EXAM_KIND`); oferecê-lo aqui era botão que sempre
    // falha. Ver primum-monolith `docs/reference/modules/content.md`
    // § Dono vs. apontador.
    .filter((exam) => readExamBody(exam).examKind === 'REAL_EXAM')
    .filter((exam) => {
      if (!term) return true;
      const body = readExamBody(exam);
      const haystack = [
        exam.id,
        // Label completo: busca por "unicamp", "SP" ou pelo nome por extenso.
        formatInstitutionLabel(body.institutionId, institutionsById),
        body.year ?? '',
        body.variant ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });

  // Sem ID nem "Criada em": no `max-w-2xl` do modal o UUID quebrava letra a
  // letra e empurrava a tabela para fora. O id continua valendo na busca.
  const columns: ColumnDef<ContentItem>[] = [
    {
      key: 'institution',
      header: 'Instituição',
      render: (row) => (
        <span className="font-medium">
          <InstitutionCell
            institutionId={readExamBody(row).institutionId}
            byId={institutionsById}
            className="max-w-[18rem]"
          />
        </span>
      ),
    },
    {
      key: 'year',
      header: 'Ano',
      render: (row) => readExamBody(row).year ?? '—',
    },
    {
      key: 'variant',
      header: 'Variante',
      render: (row) => readExamBody(row).variant || '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <ContentStatusBadge status={row.status} />,
    },
    {
      key: 'pick',
      header: '',
      render: () => (
        <span className="text-(--color-accent) text-xs font-medium">
          Escolher
        </span>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="lg"
    >
      <div className="space-y-4">
        {(data?.total ?? 0) > PICKER_PAGE_SIZE && (
          <p className="text-(--color-text-secondary) text-xs">
            {/* O total conta provas e simulados; o corte é dos itens, antes de
                tirar os simulados. */}
            Aqui entram só as {PICKER_PAGE_SIZE} provas e simulados mais
            recentes, de {data!.total}. Uma prova mais antiga ainda não se
            escolhe por este seletor.
          </p>
        )}
        <Input
          label="Buscar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="sigla, ano, variante ou id…"
        />
        {isError ? (
          // Sem isto a falha caía no `emptyMessage`, que diz que não há prova.
          <ErrorState
            message={getApiErrorMessage(
              error,
              'Não foi possível carregar as provas.',
            )}
            onRetry={() => void refetch()}
          />
        ) : (
          <Table
            columns={columns}
            data={exams}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            // Diz o filtro: numa base só de simulados, "nenhuma prova" leria como
            // se não houvesse nada, quando há — só não recebem transferência.
            emptyMessage={emptyMessage}
            striped
            onRowClick={onPick}
          />
        )}
      </div>
    </Modal>
  );
}
